<!-- Validación del estado de la conexión -->
@if (isSlowConnection) {
    <div class="alert">
        <mat-icon>wifi_off</mat-icon>
        <span>La conexión de internet es lenta.</span>
    </div>
}

<!-- ═══════════════════════════════════════════════════════════════════
     ACCESS STATE GUARDS
     Mostramos pantallas distintas según el estado del access-check.
     Solo cuando accessState() === 'granted' se renderiza el catálogo.
     ═══════════════════════════════════════════════════════════════════ -->

@switch (accessState()) {

    <!-- ── Cargando: verificando permisos ──────────────────────────── -->
    @case ('loading') {
        <div class="access-state-container">
            <mat-spinner diameter="48"></mat-spinner>
            <p class="state-text">Verificando acceso...</p>
        </div>
    }

    <!-- ── No tiene rol asignado ───────────────────────────────────── -->
    @case ('no-role') {
        <div class="access-state-container">
            <mat-icon class="state-icon warning">person_off</mat-icon>
            <h2>Sin rol asignado</h2>
            <p class="state-text">
                Tu usuario no tiene un rol asignado. Contacta al administrador
                del sistema para que te asigne uno.
            </p>
            <button mat-flat-button color="primary" (click)="goBackToMenu()">
                <mat-icon>arrow_back</mat-icon>
                Volver al menú
            </button>
        </div>
    }

    <!-- ── No existe el módulo en BD ───────────────────────────────── -->
    @case ('no-module') {
        <div class="access-state-container">
            <mat-icon class="state-icon warning">help_outline</mat-icon>
            <h2>Catálogo no registrado</h2>
            <p class="state-text">
                El catálogo <b>{{ blockedModuleLabel() }}</b> no está registrado
                como módulo en el sistema. Pide al administrador que lo agregue
                desde "Módulos".
            </p>
            <button mat-flat-button color="primary" (click)="goBackToMenu()">
                <mat-icon>arrow_back</mat-icon>
                Volver al menú
            </button>
        </div>
    }

    <!-- ── Sin permiso de LEER ─────────────────────────────────────── -->
    @case ('denied') {
        <div class="access-state-container">
            <mat-icon class="state-icon danger">lock</mat-icon>
            <h2>Sin acceso</h2>
            <p class="state-text">
                Tu rol no tiene permiso para ver <b>{{ blockedModuleLabel() }}</b>.
                Si necesitas acceso, contacta al administrador.
            </p>
            <button mat-flat-button color="primary" (click)="goBackToMenu()">
                <mat-icon>arrow_back</mat-icon>
                Volver al menú
            </button>
        </div>
    }

    <!-- ── ACCESO CONCEDIDO: render normal ─────────────────────────── -->
    @case ('granted') {
        <div class="component-container">

            <!-- ── Encabezado: solo el título, centrado ──────────── -->
            <app-page-title [title]="pageTitle || '...'" backRoute="/catalogues/menu" backTooltip="Volver al menú de catálogos"></app-page-title>
<app-page-loader [load]="pageLoad" [label]="pageTitle || '...'" (retry)="init()"></app-page-loader>

            <!-- ── Filtros ────────────────────────────────────────── -->
            <div class="filter-bar filter-bar--flush">
                <div class="filter-card">
                    <app-search-pill placeholder="Buscar en el catálogo…" (search)="applyFilter($event)"></app-search-pill>
                    @if (supportsCardView()) {
                        <mat-button-toggle-group class="view-toggle" [value]="viewMode"
                                (change)="setViewMode($event.value)"
                                aria-label="Modo de vista">
                            <mat-button-toggle value="table" matTooltip="Vista de tabla">
                                <mat-icon>table_rows</mat-icon>
                            </mat-button-toggle>
                            <mat-button-toggle value="cards" matTooltip="Vista de tarjetas">
                                <mat-icon>grid_view</mat-icon>
                            </mat-button-toggle>
                        </mat-button-toggle-group>
                    }
                </div>
                <app-filter-chips [count]="dataSource.filteredData.length" [total]="dataSource.data.length"></app-filter-chips>
            </div>

            <!-- ── Acciones: arriba de la tabla, debajo de los filtros ── -->
            <div class="list-actions">
                <button mat-stroked-button color="primary" [disabled]="!createBtn" (click)="pageLoad.refresh(getEntityData())"><mat-icon>refresh</mat-icon> Actualizar
                </button>
                <button mat-stroked-button [matMenuTriggerFor]="menu">
                    <mat-icon>table_view</mat-icon> Excel
                </button>
                <mat-menu #menu="matMenu">
                    @if (permissions['create']) {
                        <button mat-menu-item (click)="downloadSampleFile()">
                            <mat-icon>description</mat-icon>
                            <span>Archivo muestra (xlsx)</span>
                        </button>
                        <button mat-menu-item (click)="openUploadFile()">
                            <mat-icon>upload</mat-icon>
                            <span>Cargar archivo (xlsx)</span>
                        </button>
                    }
                    <button mat-menu-item (click)="downloadFile()">
                        <mat-icon>download</mat-icon>
                        <span>Descargar archivo (xlsx)</span>
                    </button>
                </mat-menu>
                @if (permissions['create']) {
                    <button mat-flat-button color="primary" class="create-btn"
                            [disabled]="!createBtn" (click)="openDynamicDialog('create')">
                        <mat-icon>add</mat-icon>
                        Nuevo
                    </button>
                }
            </div>

            <!-- ── Toolbar contextual: aparece cuando hay selección ── -->
            @if (selection.hasValue()) {
                <div class="bulk-actions-toolbar">
                    <div class="bulk-info">
                        <mat-icon>check_circle</mat-icon>
                        <span><b>{{ selectedCount }}</b> seleccionados</span>
                    </div>
                    <div class="bulk-actions">
                        @if (permissions['update']) {
                            <button mat-stroked-button color="primary"
                                    matTooltip="Activar registros seleccionados"
                                    (click)="bulkSetActive(true)">
                                <mat-icon>toggle_on</mat-icon>
                                Activar
                            </button>
                            <button mat-stroked-button
                                    matTooltip="Desactivar registros seleccionados"
                                    (click)="bulkSetActive(false)">
                                <mat-icon>toggle_off</mat-icon>
                                Desactivar
                            </button>
                            <button mat-stroked-button color="primary"
                                    matTooltip="Editar un campo en los seleccionados"
                                    (click)="openBulkEditDialog()">
                                <mat-icon>edit_note</mat-icon>
                                Editar campo
                            </button>
                        }
                        <button mat-stroked-button
                                matTooltip="Exportar selección a Excel"
                                (click)="exportSelectionToExcel()">
                            <mat-icon>file_download</mat-icon>
                            Exportar
                        </button>
                        @if (permissions['delete']) {
                            <button mat-stroked-button color="warn"
                                    matTooltip="Eliminar registros seleccionados"
                                    (click)="bulkDelete()">
                                <mat-icon>delete</mat-icon>
                                Eliminar
                            </button>
                        }
                        <button mat-icon-button matTooltip="Cancelar selección"
                                (click)="selection.clear()">
                            <mat-icon>close</mat-icon>
                        </button>
                    </div>
                </div>
            }

            <!-- ── Indicadores ────────────────────────────────────── -->
            <div class="indicators">
                <div class="total-chip">
                    <mat-icon>list_alt</mat-icon>
                    <span><b>{{ dataSource ? dataSource.filteredData.length : 0 }}</b> registros</span>
                </div>
                <mat-paginator [pageSize]="10" [pageSizeOptions]="[5, 10, 25, 50]" showFirstLastButtons></mat-paginator>
            </div>

            <!-- ── Tabla ──────────────────────────────────────────── -->
            @if (viewMode === 'table' || !supportsCardView()) {
            <div class="table-container">
                <table mat-table [dataSource]="dataSource">
                    @for (key of keys; track $index) {
                        @if (key === 'select') {
                            <ng-container matColumnDef="select">
                                <th mat-header-cell *matHeaderCellDef class="select-cell">
                                    <mat-checkbox
                                        color="primary"
                                        (change)="$event ? toggleAllRows() : null"
                                        [checked]="isAllSelected()"
                                        [indeterminate]="isPartiallySelected()"
                                        matTooltip="Seleccionar todos los visibles">
                                    </mat-checkbox>
                                </th>
                                <td mat-cell *matCellDef="let row" class="select-cell">
                                    <mat-checkbox
                                        color="primary"
                                        (click)="$event.stopPropagation()"
                                        (change)="$event ? toggleRow(row) : null"
                                        [checked]="isRowSelected(row)">
                                    </mat-checkbox>
                                </td>
                            </ng-container>
                        } @else if (key === 'more') {
                            <ng-container matColumnDef="more">
                                <th mat-header-cell *matHeaderCellDef></th>
                                <td mat-cell *matCellDef="let element; let i = index">
                                    <div class="row-actions">
                                        @if (usesSsaRegistry()) {
                                            <button mat-icon-button color="accent" matTooltip="Registros SSA"
                                                    (click)="openSsaDialog(element)">
                                                <mat-icon>verified</mat-icon>
                                            </button>
                                        }
                                        @if (usesConfigurations()) {
                                            <button mat-icon-button color="accent" matTooltip="Configuraciones"
                                                    (click)="openConfigurationsDialog(element)">
                                                <mat-icon>tune</mat-icon>
                                            </button>
                                        }
                                        @if (usesStorage()) {
                                            <button mat-icon-button color="primary" matTooltip="Almacenamiento y dimensiones"
                                                    (click)="openStorageDialog(element)">
                                                <mat-icon>inventory_2</mat-icon>
                                            </button>
                                        }
                                        @if (permissions['update']) {
                                            <button mat-icon-button color="primary" matTooltip="Editar {{ cat }}"
                                                    (click)="openDynamicDialog('update', element)">
                                                <mat-icon>edit</mat-icon>
                                            </button>
                                        }
                                        @if (permissions['delete']) {
                                            <button mat-icon-button color="warn" matTooltip="Eliminar {{ cat }}"
                                                    (click)="deleteRecord(element, i)">
                                                <mat-icon>delete</mat-icon>
                                            </button>
                                        }
                                    </div>
                                </td>
                            </ng-container>
                         } @else {
    <ng-container [matColumnDef]="key">
        <th mat-header-cell *matHeaderCellDef>{{ keyNames[$index] }}</th>
        <td mat-cell *matCellDef="let element; let i = index">

            @if (key === 'active') {
                <!-- Toggle interactivo. Solo clickeable si tiene permiso EDITAR. -->
                <mat-slide-toggle
                    [checked]="element.active"
                    [disabled]="!permissions['update']"
                    (change)="toggleActiveField(element, i)"
                    color="primary"
                    [matTooltip]="element.active ? 'Activo' : 'Inactivo'">
                </mat-slide-toggle>

            } @else if (isFileUploadColumn(key)) {
                <!-- Columnas con archivos (logo, manuales, datasheet, etc.):
                     muestran botón Ver (si ya hay archivo) + Subir/Reemplazar. -->
                <div class="file-upload-cell">
                    @if (isUploadingFile(element, key)) {
                        <mat-spinner diameter="20"></mat-spinner>
                    } @else {
                        @if (element[key]) {
                            @if (fileUploadMeta(key).isImage) {
                                <!-- La miniatura necesita una URL FIRMADA: lo que se
                                     guarda es la key de S3. Si aún no se resolvió (o
                                     falló), se muestra el icono para no dejar la
                                     celda con una imagen rota. -->
                                @if (imageUrlOf(element, key); as imgUrl) {
                                    <img class="file-upload-thumb"
                                         [src]="imgUrl"
                                         [alt]="fileUploadMeta(key).label"
                                         (click)="viewUploadedFile(element, key)"
                                         matTooltip="Ver {{ fileUploadMeta(key).label }}">
                                } @else {
                                    <button mat-icon-button color="primary"
                                            matTooltip="Ver {{ fileUploadMeta(key).label }}"
                                            (click)="viewUploadedFile(element, key)">
                                        <mat-icon>{{ fileUploadMeta(key).icon }}</mat-icon>
                                    </button>
                                }
                            } @else {
                                <button mat-icon-button color="primary"
                                        matTooltip="Ver {{ fileUploadMeta(key).label }}"
                                        (click)="viewUploadedFile(element, key)">
                                    <mat-icon>{{ fileUploadMeta(key).icon }}</mat-icon>
                                </button>
                            }
                        }
                        @if (permissions['update']) {
                            <button mat-icon-button color="accent"
                                    [matTooltip]="(element[key] ? 'Reemplazar ' : 'Cargar ') + fileUploadMeta(key).label"
                                    (click)="triggerFileUpload(element, key)">
                                <mat-icon>{{ element[key] ? 'cloud_sync' : 'cloud_upload' }}</mat-icon>
                            </button>
                        }
                    }
                </div>

            } @else if (key === 'color') {
                <!-- Color de cilindro: cuadrito con el hex guardado + nombre. -->
                @if (element[key]) {
                    <span class="color-swatch-cell">
                        <span class="color-swatch" [style.background]="colorHex(element[key])"></span>
                        <span>{{ colorLabel(element[key]) }}</span>
                    </span>
                }

            } @else if (key === 'storageType') {
                <!-- Lockers: tipo capturado (vacío en registros viejos = Locker). -->
                {{ element[key] === 'INHOUSE' ? 'Inhouse' : 'Locker' }}

            } @else if (isResolvedIdColumn(key)) {
                <!-- Columnas que son IDs de otras entidades: se resuelven
                     a su label via changeIds() y se guardan como `_<key>`. -->
                {{ element['_' + key] ?? '' }}

            } @else {
                <!-- Resto: texto, número, fecha, email, booleano, etc.
                     Se leen directo del registro. Vacío = celda vacía. -->
                {{ element[key] ?? '' }}
            }
        </td>
    </ng-container>
}
                    }

                    <tr mat-header-row *matHeaderRowDef="keys; sticky: true"></tr>
                    <tr mat-row *matRowDef="let row; columns: keys;" class="table-row"></tr>
                </table>

                @if (dataSource && dataSource.filteredData.length === 0) {
                    <div class="no-data">
                        <mat-icon>search_off</mat-icon>
                        <p>No se encontraron registros</p>
                    </div>
                }
            </div>
            }

            <!-- ── Cartas ─────────────────────────────────────────── -->
            @if (viewMode === 'cards' && supportsCardView()) {
                <div class="cards-container">
                    @if (dataSource && dataSource.filteredData.length === 0) {
                        <div class="no-data">
                            <mat-icon>search_off</mat-icon>
                            <p>No se encontraron registros</p>
                        </div>
                    } @else {
                        <div class="cards-grid">
                            @for (element of cardData; track element.id; let i = $index) {
                                <div class="catalogue-card" [class.catalogue-card--selected]="isRowSelected(element)">
                                    @if (hasImageColumn) {
                                        <div class="card-photo">
                                            @if (imageUrlOf(element, 'image'); as imgUrl) {
                                                <img [src]="imgUrl" [alt]="fileUploadMeta('image').label" loading="lazy"
                                                     (click)="viewUploadedFile(element, 'image')" matTooltip="Ver {{ fileUploadMeta('image').label }}">
                                            } @else {
                                                <mat-icon>image_not_supported</mat-icon>
                                            }
                                        </div>
                                    }
                                    <div class="card-header">
                                        <mat-checkbox color="primary" [checked]="isRowSelected(element)" (change)="toggleRow(element)" matTooltip="Seleccionar"></mat-checkbox>
                                        <span class="card-title">{{ cardTitle(element) || '—' }}</span>
                                        @if (keys.includes('active')) {
                                            <mat-slide-toggle
                                                [checked]="element.active"
                                                [disabled]="!permissions['update']"
                                                (change)="toggleActiveField(element, i)"
                                                color="primary"
                                                [matTooltip]="element.active ? 'Activo' : 'Inactivo'">
                                            </mat-slide-toggle>
                                        }
                                    </div>

                                    <div class="card-body">
                                        @for (key of cardFieldKeys(); track key) {
                                            <div class="card-field">
                                                <span class="card-field-label">{{ keyNameFor(key) }}:</span>
                                                @if (key === 'color') {
                                                    @if (element[key]) {
                                                        <span class="card-field-value color-swatch-cell">
                                                            <span class="color-swatch" [style.background]="colorHex(element[key])"></span>
                                                            <span>{{ colorLabel(element[key]) }}</span>
                                                        </span>
                                                    } @else {
                                                        <span class="card-field-value">—</span>
                                                    }
                                                } @else {
                                                    <span class="card-field-value">{{ cardValue(element, key) || '—' }}</span>
                                                }
                                            </div>
                                        }
                                    </div>

                                    <div class="card-actions">
                                        <!-- Archivos (foto, manuales, ficha técnica…): ver y cargar/reemplazar, igual que en la tabla -->
                                        @for (fk of cardFileKeys(); track fk) {
                                            @if (isUploadingFile(element, fk)) {
                                                <mat-spinner diameter="20"></mat-spinner>
                                            } @else {
                                                @if (element[fk] && fk !== 'image') {
                                                    <button mat-icon-button color="primary" matTooltip="Ver {{ fileUploadMeta(fk).label }}"
                                                            (click)="viewUploadedFile(element, fk)">
                                                        <mat-icon>{{ fileUploadMeta(fk).icon }}</mat-icon>
                                                    </button>
                                                }
                                                @if (permissions['update']) {
                                                    <button mat-icon-button color="accent"
                                                            [matTooltip]="(element[fk] ? 'Reemplazar ' : 'Cargar ') + fileUploadMeta(fk).label"
                                                            (click)="triggerFileUpload(element, fk)">
                                                        <mat-icon>{{ element[fk] ? 'cloud_sync' : 'cloud_upload' }}</mat-icon>
                                                    </button>
                                                }
                                            }
                                        }
                                        @if (usesSsaRegistry()) {
                                            <button mat-icon-button color="accent" matTooltip="Registros SSA"
                                                    (click)="openSsaDialog(element)">
                                                <mat-icon>verified</mat-icon>
                                            </button>
                                        }
                                        @if (usesConfigurations()) {
                                            <button mat-icon-button color="accent" matTooltip="Configuraciones"
                                                    (click)="openConfigurationsDialog(element)">
                                                <mat-icon>tune</mat-icon>
                                            </button>
                                        }
                                        @if (usesStorage()) {
                                            <button mat-icon-button color="primary" matTooltip="Almacenamiento y dimensiones"
                                                    (click)="openStorageDialog(element)">
                                                <mat-icon>inventory_2</mat-icon>
                                            </button>
                                        }
                                        @if (permissions['update']) {
                                            <button mat-icon-button color="primary" matTooltip="Editar {{ cat }}"
                                                    (click)="openDynamicDialog('update', element)">
                                                <mat-icon>edit</mat-icon>
                                            </button>
                                        }
                                        @if (permissions['delete']) {
                                            <button mat-icon-button color="warn" matTooltip="Eliminar {{ cat }}"
                                                    (click)="deleteRecord(element, i)">
                                                <mat-icon>delete</mat-icon>
                                            </button>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                        @if (cardTotal > cardData.length) {
                            <div class="cards-more">
                                <span>Mostrando {{ cardData.length }} de {{ cardTotal }}</span>
                                <button mat-stroked-button color="primary" (click)="showMoreCards()"><mat-icon>expand_more</mat-icon> Ver más</button>
                            </div>
                        }
                    }
                </div>
            }

        </div>
    }
}